import React, { useState, useEffect, useMemo } from 'react';
import { promocodeService } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiGrid, FiList, FiSearch } from 'react-icons/fi';
import './PromoCodes.css';

const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PromoCodes = () => {
  const [promocodes, setPromocodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percent',
    discount_value: '',
    valid_from: '',
    valid_to: '',
    max_uses: '',
    is_active: true,
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await promocodeService.getAll();
      const data = response.data;
      setPromocodes(toList(data));
    } catch (error) {
      console.error('Error fetching promocodes:', error);
      setPromocodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses, 10) : null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_to: formData.valid_to ? new Date(formData.valid_to).toISOString() : null,
      };
      if (editingPromo) {
        await promocodeService.update(editingPromo.id, payload);
      } else {
        await promocodeService.create(payload);
      }
      setShowModal(false);
      setEditingPromo(null);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      console.error('Error saving promocode:', error);
      alert(error.response?.data?.code?.[0] || error.response?.data?.detail || 'Error saving promocode');
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      description: promo.description || '',
      discount_type: promo.discount_type || 'percent',
      discount_value: promo.discount_value ?? '',
      valid_from: promo.valid_from ? promo.valid_from.slice(0, 16) : '',
      valid_to: promo.valid_to ? promo.valid_to.slice(0, 16) : '',
      max_uses: promo.max_uses ?? '',
      is_active: promo.is_active !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promocode?')) {
      try {
        await promocodeService.delete(id);
        fetchPromoCodes();
      } catch (error) {
        console.error('Error deleting promocode:', error);
        alert('Error deleting promocode');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percent',
      discount_value: '',
      valid_from: '',
      valid_to: '',
      max_uses: '',
      is_active: true,
    });
  };

  const filteredPromocodes = useMemo(() => {
    let list = promocodes;
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.code && p.code.toLowerCase().includes(q)) ||
          (p.description && p.description.toLowerCase().includes(q))
      );
    }
    if (statusFilter === 'active') list = list.filter((p) => p.is_active === true);
    if (statusFilter === 'inactive') list = list.filter((p) => p.is_active === false);
    return list;
  }, [promocodes, searchQuery, statusFilter]);

  if (loading) {
    return <div className="loading">Loading promo codes...</div>;
  }

  return (
    <div className="promocodes-page">
      <div className="page-header">
        <h1>Promo Codes</h1>
        <div className="page-header-actions">
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <FiGrid />
              <span>Cards</span>
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FiList />
              <span>List</span>
            </button>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setShowModal(true);
              setEditingPromo(null);
              resetForm();
            }}
          >
            <FiPlus />
            Add Promo Code
          </button>
        </div>
      </div>

      <div className="promocodes-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by promo code or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-wrap">
          <label htmlFor="status-filter" className="filter-label">Status</label>
          <select
            id="status-filter"
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="promocodes-list-wrap">
          <table className="promocodes-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Valid from</th>
                <th>Valid to</th>
                <th>Used</th>
                <th>Status</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPromocodes.length === 0 ? (
                <tr>
                  <td colSpan="8" className="td-empty">
                    {promocodes.length === 0
                      ? 'No promo codes yet. Add one to get started.'
                      : 'No promo codes match your search or filter.'}
                  </td>
                </tr>
              ) : (filteredPromocodes.map((promo) => (
                <tr key={promo.id}>
                  <td className="td-code">{promo.code}</td>
                  <td className="td-desc">{promo.description || '—'}</td>
                  <td className="td-value">
                    {promo.discount_type === 'percent'
                      ? `${promo.discount_value}% off`
                      : `₹${parseFloat(promo.discount_value).toFixed(2)} off`}
                  </td>
                  <td>{formatDate(promo.valid_from)}</td>
                  <td>{formatDate(promo.valid_to)}</td>
                  <td>{promo.used_count}{promo.max_uses != null ? ` / ${promo.max_uses}` : ''}</td>
                  <td>
                    {promo.is_active ? (
                      <span className="badge active">Active</span>
                    ) : (
                      <span className="badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="td-actions">
                    <button className="btn-edit" onClick={() => handleEdit(promo)} title="Edit">
                      <FiEdit2 />
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(promo.id)} title="Delete">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="promocodes-grid">
        {filteredPromocodes.map((promo) => (
          <div key={promo.id} className="promo-card">
            <div className="promo-info">
              <h3 className="promo-code">{promo.code}</h3>
              {promo.description && <p className="promo-desc">{promo.description}</p>}
              <div className="promo-value">
                {promo.discount_type === 'percent'
                  ? `${promo.discount_value}% off`
                  : `₹${parseFloat(promo.discount_value).toFixed(2)} off`}
              </div>
              <div className="promo-meta">
                <span>Valid: {formatDate(promo.valid_from)} → {formatDate(promo.valid_to)}</span>
                <span>Used: {promo.used_count}{promo.max_uses != null ? ` / ${promo.max_uses}` : ''}</span>
              </div>
              <div className="promo-badges">
                {promo.is_active ? (
                  <span className="badge active">Active</span>
                ) : (
                  <span className="badge inactive">Inactive</span>
                )}
              </div>
            </div>
            <div className="promo-actions">
              <button className="btn-edit" onClick={() => handleEdit(promo)} title="Edit">
                <FiEdit2 />
              </button>
              <button className="btn-delete" onClick={() => handleDelete(promo.id)} title="Delete">
                <FiTrash2 />
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {filteredPromocodes.length === 0 && !loading && (
        <p className="no-data">
          {promocodes.length === 0
            ? 'No promo codes yet. Add one to get started.'
            : 'No promo codes match your search or filter.'}
        </p>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowModal(false);
            setEditingPromo(null);
            resetForm();
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPromo ? 'Edit Promo Code' : 'Add Promo Code'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SAVE20"
                  required
                  disabled={!!editingPromo}
                />
                {editingPromo && <small>Code cannot be changed after creation.</small>}
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Short description"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  >
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{formData.discount_type === 'percent' ? 'Percentage (0–100)' : 'Amount (₹)'} *</label>
                  <input
                    type="number"
                    min="0"
                    step={formData.discount_type === 'percent' ? '1' : '0.01'}
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Valid from</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Valid to</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_to}
                    onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row form-row-actions">
                <div className="form-group">
                  <label>Max uses (empty = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPromo(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingPromo ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodes;
